import {useCallback, useEffect, useRef, useState} from 'react'

import './AppSelect.scss'

const AppSelect = ({
  ariaLabel,
  className = '',
  disabled = false,
  label,
  loading = false,
  onChange,
  options = [],
  placeholder = '-',
  value,
}) => {
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef(null)
  const isDisabled = disabled || loading
  const selectedOption = options.find((option) => option.value === value)
  const resolvedAriaLabel = ariaLabel || label || placeholder

  useEffect(() => {
    if (isDisabled) {
      setOpen(false)
    }
  }, [isDisabled])

  useEffect(() => {
    if (!open) {
      return undefined
    }

    const handlePointerDown = (event) => {
      if (!wrapperRef.current?.contains(event.target)) {
        setOpen(false)
      }
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false)
      }
    }

    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open])

  const handleSelect = useCallback(
    (option) => {
      if (isDisabled || option.disabled) {
        return
      }

      onChange(option.value)
      setOpen(false)
    },
    [isDisabled, onChange]
  )

  const handleButtonKeyDown = (event) => {
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      setOpen(true)
    }
  }

  return (
    <div
      className={`app-select ${open ? 'open' : ''} ${isDisabled ? 'is-disabled' : ''} ${
        className || ''
      }`.trim()}
      ref={wrapperRef}
    >
      {label && <span className='app-select__label'>{label}</span>}
      <button
        type='button'
        className='app-select__button'
        aria-label={resolvedAriaLabel}
        aria-expanded={open}
        aria-haspopup='listbox'
        disabled={isDisabled}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={handleButtonKeyDown}
      >
        <span className={selectedOption ? '' : 'is-placeholder'}>
          {selectedOption?.label || placeholder}
        </span>
        {loading ? (
          <span className='app-select__spinner' aria-hidden='true' />
        ) : (
          <span className='app-select__chevron' aria-hidden='true' />
        )}
      </button>

      {open && !isDisabled && options.length > 0 && (
        <div className='app-select__menu' role='listbox'>
          {options.map((option) => (
            <button
              type='button'
              role='option'
              aria-selected={option.value === value}
              disabled={option.disabled}
              className={`app-select__option ${option.value === value ? 'active' : ''}`.trim()}
              key={option.value}
              onClick={() => handleSelect(option)}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default AppSelect
